import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, MapPin, Star } from "lucide-react";

import { api } from "@/api";

const fallbackImage = "https://images.unsplash.com/photo-1542992933-ce75d0187ec1?auto=format&fit=crop&w=1200&q=85";

const mediaUrl = (source) => {
  if (!source) return fallbackImage;
  if (source.startsWith("http")) return source;
  return `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/media/${source}`;
};

const replaceBrokenImage = (event) => {
  if (event.currentTarget.src !== fallbackImage) event.currentTarget.src = fallbackImage;
};

export default function PublicProfilePage({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const visitorKey = "crewed-portfolio-visitor";
    const visitorId = localStorage.getItem(visitorKey) || crypto.randomUUID();
    localStorage.setItem(visitorKey, visitorId);
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source") || (document.referrer ? new URL(document.referrer).hostname : "direct");
    const campaign = params.get("campaign") || "organic";
    api.get(`/freelancers/${slug}`, { params: { visitor_id: visitorId, source, campaign } })
      .then(({ data: profileData }) => setData(profileData))
      .catch(() => setError("Portfolio not found."));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const { profile, reviews } = data;
    const portfolio = profile.portfolio || {};
    const image = portfolio.preview_style === "minimal"
      ? `${process.env.REACT_APP_BACKEND_URL}/api/freelancers/${profile.profile_slug}/social-card.svg`
      : mediaUrl(portfolio.preview_image || portfolio.gallery?.[0]);
    const rating = reviews.length
      ? (reviews.reduce((total, review) => total + review.score, 0) / reviews.length).toFixed(1)
      : portfolio.rating_average || "New";
    const description = `${portfolio.headline || "Event freelancer"} · ★ ${rating} rated talent`;
    const tags = [
      ["property", "og:title", `${profile.name} — Crewed event talent`],
      ["property", "og:description", description],
      ["property", "og:image", image],
      ["property", "og:url", window.location.href],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", `${profile.name} — Crewed event talent`],
      ["name", "twitter:description", description],
      ["name", "twitter:image", image],
    ];
    document.title = `${profile.name} — Crewed event talent`;
    tags.forEach(([attribute, key, content]) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    });
  }, [data]);

  if (error) return <main className="public-profile-page" data-testid="public-profile-error">{error}</main>;
  if (!data) return <main className="public-profile-page" data-testid="public-profile-loading">Loading profile…</main>;

  const { profile, reviews } = data;
  const portfolio = profile.portfolio || {};
  const ratingAverage = reviews.length
    ? (reviews.reduce((total, review) => total + review.score, 0) / reviews.length).toFixed(1)
    : portfolio.rating_average || "New";
  const gallery = portfolio.gallery?.length ? portfolio.gallery : [fallbackImage];
  const inquiryLink = user?.role === "manager" ? `/messages?with=${profile.id}` : "/join/manager";
  const sendInquiry = async () => {
    if (user?.role !== "manager") {
      navigate(inquiryLink);
      return;
    }
    await api.post(`/freelancers/${slug}/inquiry-click`);
    navigate(inquiryLink);
  };

  return <main className="public-profile-page" data-testid="public-profile-page">
    <Link className="back-link" data-testid="public-profile-back-link" to="/talent"><ArrowLeft size={16} /> Talent directory</Link>
    <header className="public-profile-hero">
      <div><p className="eyebrow">EVENT TALENT PORTFOLIO</p><h1 data-testid="public-profile-name">{profile.name}</h1><p data-testid="public-profile-headline">{portfolio.headline}</p></div>
      <div className="profile-score"><Star fill="currentColor" size={19} /><strong data-testid="public-profile-rating">{ratingAverage}</strong><span data-testid="public-profile-review-count">{reviews.length} client reviews</span></div>
    </header>
    <section className="public-profile-overview">
      <div><p className="eyebrow">ABOUT</p><p data-testid="public-profile-bio">{portfolio.bio || "Available for exceptional live events."}</p><div className="profile-tags">{(portfolio.skills || []).map((skill) => <span data-testid={`public-profile-skill-${skill}`} key={skill}>{skill}</span>)}</div></div>
      <aside><p className="eyebrow">AVAILABILITY</p><p data-testid="public-profile-availability"><CalendarCheck size={16} />{portfolio.availability || "Available"}</p><p data-testid="public-profile-rate"><MapPin size={16} />From ${portfolio.starting_rate || 0}</p><button className="primary-button" data-testid="public-profile-inquiry-link" onClick={sendInquiry}>{user?.role === "manager" ? "Send inquiry" : "Inquire as a manager"}</button></aside>
    </section>
    <section className="public-gallery-section"><p className="eyebrow">SELECTED EVENT WORK</p><div className={`public-gallery ${gallery.length === 1 ? "single" : ""}`} data-testid="public-profile-gallery">{gallery.map((image, index) => <img alt={`${profile.name} event work ${index + 1}`} data-testid={`public-gallery-image-${index}`} key={image} onError={replaceBrokenImage} src={mediaUrl(image)} />)}</div></section>
    <section className="reviews-section"><p className="eyebrow">CLIENT REVIEWS</p><h2>Trusted in the room.</h2><div className="reviews-list" data-testid="public-profile-reviews">{reviews.length ? reviews.map((review) => <article data-testid={`public-review-${review.id}`} key={review.id}><Star fill="currentColor" size={15} /><blockquote>{review.comment}</blockquote><strong>{review.manager_name}</strong><span>{review.job_title}</span></article>) : <p data-testid="public-profile-no-reviews">New on Crewed — reviews will appear after completed events.</p>}</div></section>
  </main>;
}